<template>
  <div class="blog-post">
    <!-- Blog Header -->
    <div class="jumbotron-fluid bg-ctdark py-4">
      <div class="container">
        <div class="row">
          <div class="col-md-12 col-lg-4"></div>
          <div class="col-md-12 col-lg-4 text-center pb-2">
            <h1 class="text-blog-title text-light pt-0">{{ t('blog.title') }}</h1>
          </div>
          <div class="col-md-12 col-lg-4"></div>
        </div>
      </div>
    </div>

    <div class="jumbotron-fluid py-3 bg-ctmain">
      <div class="container">
        <div class="row">
          <!-- Blog Post Content -->
          <div class="col-md-8 card mb-4 mt-3 left top">
            <div v-if="loading" class="text-center p-5">
              <div class="spinner-border text-ctorange" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2">Loading blog post...</p>
            </div>

            <div v-else-if="error" class="alert alert-danger">
              <p><strong>Error loading blog post:</strong></p>
              <pre>{{ JSON.stringify(error, null, 2) }}</pre>
              <router-link to="/blog" class="btn btn-ctl mt-3">&larr; {{ t('blog.backToBlog') }}</router-link>
            </div>

            <div v-else-if="!currentPost" class="text-center p-5">
              <p>Post not found!</p>
              <router-link to="/blog" class="btn btn-ctl mt-3">&larr; {{ t('blog.backToBlog') }}</router-link>
            </div>

            <div v-else class="card-body">
              <h1 class="text-blog-title text-white">{{ displayTitle }}</h1>
              <p class="text-muted small">
                <i class="fa fa-clock-o"></i> {{ formatDate(currentPost.created_on) }}
                <span v-if="currentPost.author_name">
                  - posted by {{ currentPost.author_name }}
                </span>
              </p>
              <div class="card-text text-white" v-html="displayContent"></div>
              <router-link to="/blog" class="btn btn-ctl mt-4">&larr; {{ t('blog.backToBlog') }}</router-link>
            </div>
          </div>

          <!-- Sidebar -->
          <div class="col-md-4 float-right">
            <div class="card my-4">
              <h5 class="card-header">{{ t('blog.recentPosts') }}</h5>
              <div v-for="post in recentPosts" :key="post.id" class="card-body py-2">
                <p class="card-text">
                  <router-link
                    :to="{ name: 'blog-post', params: { slug: post.slug } }"
                    class="text-decoration-none text-white"
                    :class="{'text-ctorange': currentPost && post.id === currentPost.id}"
                  >
                    <i v-if="currentPost && post.id === currentPost.id" class="fas fa-caret-right text-ctorange"></i>
                    {{ getTranslatedTitle(post) }}
                  </router-link>
                  <span class="font-italic small ms-2 text-muted">{{ formatDate(post.created_on) }}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n'
import { mapState, mapActions } from 'vuex';

export default {
  name: 'BlogPostView',
  setup() {
    const { t, locale } = useI18n()
    return { t, locale }
  },
  computed: {
    ...mapState('blog', ['posts', 'currentPost', 'loading', 'error']),
    recentPosts() {
      return [...this.posts].sort((a, b) =>
        new Date(b.created_on) - new Date(a.created_on)
      ).slice(0, 5);
    },
    displayTitle() {
      if (!this.currentPost) return '';

      // Check if there's a translation in the current language
      const currentLanguage = this.$i18n.locale;
      if (currentLanguage !== this.currentPost.language) {
        const translation = this.currentPost.translations?.find(
          t => t.language === currentLanguage
        );

        if (translation) {
          return translation.title;
        }
      }

      return this.currentPost.title;
    },
    displayContent() {
      if (!this.currentPost) return '';

      // Check if there's a translation in the current language
      const currentLanguage = this.$i18n.locale;
      if (currentLanguage !== this.currentPost.language) {
        const translation = this.currentPost.translations?.find(
          t => t.language === currentLanguage
        );

        if (translation) {
          return translation.content;
        }
      }

      return this.currentPost.content;
    }
  },
  methods: {
    ...mapActions('blog', ['fetchPost', 'fetchPosts']),
    formatDate(dateString) {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    getTranslatedTitle(post) {
      const currentLanguage = this.$i18n.locale;
      if (currentLanguage !== post.language) {
        const translation = post.translations?.find(t => t.language === currentLanguage);
        if (translation) {
          return translation.title;
        }
      }
      return post.title;
    }
  },
  watch: {
    // Watch for changes in the URL parameter
    '$route.params.slug': {
      handler(slug) {
        if (slug) {
          this.fetchPost(slug).catch(error => {
            console.error('Error fetching blog post:', error);
          });
        }
      },
      immediate: true
    }
  },
  created() {
    // If we don't have posts loaded yet, load them for the sidebar
    if (this.posts.length === 0) {
      this.fetchPosts().catch(error => {
        console.error('Error fetching blog posts:', error);
      });
    }

    // Load the current post
    const { slug } = this.$route.params;
    if (slug) {
      this.fetchPost(slug).catch(error => {
        console.error('Error fetching blog post:', error);
      });
    }
  }
}
</script>

<style scoped>
.text-blog-title {
  font-size: 2.5rem;
  font-weight: 700;
}

.card {
  border: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  background-color: var(--ct-dark-tertiary);
}

.card-header {
  background-color: var(--ct-dark);
  color: white;
}

.card-body {
  color: #e0e0e0;
}

.card-title {
  color: white;
}

.card-text {
  color: #e0e0e0;
}

.btn-ctl {
  background-color: var(--ct-orange);
  color: #000;
  font-weight: 600;
}

.btn-ctl:hover {
  background-color: var(--ct-orange-hover);
}

/* For blog content */
.card-text :deep(img) {
  max-width: 100%;
  height: auto;
}

.card-text :deep(table) {
  width: 100%;
  margin-bottom: 1rem;
  border-collapse: collapse;
}

.card-text :deep(td), .card-text :deep(th) {
  padding: 0.5rem;
  border: 1px solid #dee2e6;
}

.text-white {
  color: #ffffff !important;
}
</style>